import { Truck } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { DimensionCard } from "./DimensionCard";
import { MetricCard } from "./MetricCard";
import type { DailyTeamStats } from "../api/useTeamStats";

interface DeliverySectionProps {
  daily: DailyTeamStats[];
}

export function DeliverySection({ daily }: DeliverySectionProps) {
  const totalCompleted = daily.reduce((s, d) => s + d.itemsCompleted, 0);
  const totalCreated = daily.reduce((s, d) => s + d.itemsCreated, 0);
  const completionRate = totalCreated > 0 ? (totalCompleted / totalCreated) * 100 : 100;
  const daysWithData = daily.filter((d) => d.itemsCompleted > 0).length;
  const throughput = daysWithData > 0 ? totalCompleted / daysWithData : 0;
  const avgCycleTime = daily.length > 0
    ? daily.reduce((s, d) => s + d.avgCycleTimeMinutes, 0) / daily.length
    : 0;

  const chartData = daily.map((d) => ({
    date: new Date(d.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    completed: d.itemsCompleted,
  }));

  return (
    <DimensionCard title="Delivery" icon={Truck} colorTheme="green" id="delivery">
      <div className="grid grid-cols-3 gap-3 mb-4">
        <MetricCard label="Completion Rate" value={`${Math.round(completionRate)}%`} />
        <MetricCard label="Throughput" value={`${Math.round(throughput * 10) / 10}/day`} />
        <MetricCard label="Avg Cycle Time" value={`${Math.round(avgCycleTime)}m`} />
      </div>
      {chartData.length > 0 ? (
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ backgroundColor: "#1f2937", border: "none", borderRadius: "6px", color: "#f9fafb", fontSize: "12px" }} />
              <Bar dataKey="completed" fill="#16a34a" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex items-center justify-center h-48 bg-gray-50 dark:bg-white/5 rounded-lg">
          <p className="text-sm text-gray-400">No trend data available</p>
        </div>
      )}
    </DimensionCard>
  );
}
